import { Box, Button, Chip, MenuItem, Select, TextField } from '@mui/material';
import { FormEvent, useCallback, useEffect, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { PaloColumn, PaloListView } from '@synergy/components-palo';
import { tokens } from '@synergy/theme-palo';
import { useSearchParams } from 'react-router-dom';

import { SearchDictionaryEntry, SearchDictionaryScope } from '../models/search-quality';
import { useThunkDispatch } from '../../../../store/use-thunk-dispatch';

const SCOPES: SearchDictionaryScope[] = ['term', 'word'];

/**
 * Ebene 2 — Wörterbuch. The bridge between what members type and what the
 * catalogue answers to, as a list anyone who knows the range can maintain. A line
 * takes effect on the next search. A line the nightly run wrote is rejected rather
 * than removed: the row stays switched off, so the same guess is not suggested again.
 *
 * The hit count beside each line is the one figure that matters here: a
 * replacement that finds nothing has turned one empty answer into another.
 */
export const SearchDictionaryView = () => {
    const dispatch = useThunkDispatch();
    const intl = useIntl();
    const [searchParams] = useSearchParams();
    const [entries, setEntries] = useState<SearchDictionaryEntry[]>([]);
    const [requesting, setRequesting] = useState(true);
    const [scope, setScope] = useState<SearchDictionaryScope>('term');
    // A worklist row can hand its term over, so the line starts half written.
    const [term, setTerm] = useState(searchParams.get('term') ?? '');
    const [replacement, setReplacement] = useState('');
    const [saving, setSaving] = useState(false);
    const [saveFailed, setSaveFailed] = useState(false);

    const load = useCallback(() => {
        setRequesting(true);
        void dispatch(async (_dispatch, _getState, { api }) => {
            const result = await api.searchQuality.getDictionary();
            setEntries(result.success ? result.result.entries : []);
            setRequesting(false);
        });
    }, [dispatch]);

    useEffect(load, [load]);

    const save = (event: FormEvent) => {
        event.preventDefault();
        if (!term.trim() || !replacement.trim()) {
            return;
        }
        setSaving(true);
        setSaveFailed(false);
        void dispatch(async (_dispatch, _getState, { api }) => {
            const result = await api.searchQuality.saveDictionaryEntry(term, replacement, scope);
            setSaving(false);
            if (!result.success) {
                setSaveFailed(true);
                return;
            }
            setTerm('');
            setReplacement('');
            load();
        });
    };

    // Approving is saving the same line again: the backend hands it over to the
    // person doing it, and nothing withdraws it automatically afterwards.
    const approve = (entry: SearchDictionaryEntry) => {
        void dispatch(async (_dispatch, _getState, { api }) => {
            const result = await api.searchQuality.saveDictionaryEntry(entry.term, entry.replacement, entry.scope);
            if (result.success) {
                load();
            }
        });
    };

    const reject = (entry: SearchDictionaryEntry) => {
        void dispatch(async (_dispatch, _getState, { api }) => {
            const result = await api.searchQuality.rejectDictionaryEntry(entry.term, entry.scope);
            if (result.success) {
                load();
            }
        });
    };

    const remove = (entry: SearchDictionaryEntry) => {
        const before = entries;
        setEntries(current => current.filter(item => item !== entry));
        void dispatch(async (_dispatch, _getState, { api }) => {
            const result = await api.searchQuality.removeDictionaryEntry(entry.term, entry.scope);
            if (!result.success) {
                setEntries(before);
            }
        });
    };

    const columns: PaloColumn<SearchDictionaryEntry>[] = [
        {
            key: 'scope',
            type: 'classification',
            head: <FormattedMessage id="app.searchConsole.dictionary.column.scope" />,
            cell: entry => (
                <Chip
                    size="small"
                    variant="outlined"
                    label={<FormattedMessage id={`app.searchConsole.dictionary.scope.${entry.scope}`} />}
                />
            ),
        },
        {
            key: 'term',
            type: 'name',
            head: <FormattedMessage id="app.searchConsole.dictionary.column.term" />,
            cell: entry => <Box sx={{ fontWeight: 550 }}>{entry.term}</Box>,
        },
        {
            key: 'replacement',
            type: 'name',
            head: <FormattedMessage id="app.searchConsole.dictionary.column.replacement" />,
            cell: entry => entry.replacement,
        },
        {
            key: 'source',
            type: 'classification',
            head: <FormattedMessage id="app.searchConsole.dictionary.column.source" />,
            cell: entry =>
                'manual' === entry.source ? (
                    <Box component="span" sx={{ color: tokens.color.ink3 }}>
                        <FormattedMessage id="app.searchConsole.dictionary.source.manual" />
                    </Box>
                ) : (
                    <Chip
                        size="small"
                        color={'suggested' === entry.status ? 'warning' : 'default'}
                        variant={'suggested' === entry.status ? 'filled' : 'outlined'}
                        title={entry.evidence ?? undefined}
                        label={
                            <FormattedMessage
                                id={`app.searchConsole.dictionary.status.${
                                    'suggested' === entry.status ? 'suggested' : entry.status
                                }`}
                            />
                        }
                    />
                ),
        },
        {
            key: 'hits',
            head: <FormattedMessage id="app.searchConsole.dictionary.column.hits" />,
            align: 'right',
            variant: 'amount',
            width: '9%',
            cell: entry =>
                entry.hits > 0 ? (
                    entry.hits.toLocaleString('de-AT')
                ) : (
                    <Box component="span" sx={{ color: tokens.color.warnLine, fontFamily: 'inherit' }}>
                        <FormattedMessage id="app.searchConsole.dictionary.noHits" />
                    </Box>
                ),
        },
        {
            key: 'createdAt',
            type: 'date',
            head: <FormattedMessage id="app.searchConsole.dictionary.column.createdAt" />,
            cell: entry => new Date(entry.createdAt).toLocaleDateString('de-AT'),
        },
        {
            key: 'remove',
            type: 'icon',
            head: '',
            cell: entry => {
                // A person's own line can simply go; one the nightly run wrote is
                // rejected, so the term stays taken.
                if ('manual' === entry.source) {
                    return (
                        <Button size="small" variant="text" onClick={() => remove(entry)} sx={{ fontSize: '12px' }}>
                            <FormattedMessage id="app.searchConsole.dictionary.remove" />
                        </Button>
                    );
                }

                return (
                    <Box sx={{ display: 'flex', gap: '2px', justifyContent: 'flex-end' }}>
                        {'active' !== entry.status ? (
                            <Button
                                size="small"
                                variant="text"
                                onClick={() => approve(entry)}
                                sx={{ fontSize: '12px' }}
                            >
                                <FormattedMessage id="app.searchConsole.dictionary.approve" />
                            </Button>
                        ) : null}
                        {'retired' !== entry.status ? (
                            <Button
                                size="small"
                                variant="text"
                                color="inherit"
                                onClick={() => reject(entry)}
                                sx={{ fontSize: '12px', color: tokens.color.ink3 }}
                            >
                                <FormattedMessage id="app.searchConsole.dictionary.discard" />
                            </Button>
                        ) : null}
                    </Box>
                );
            },
        },
    ];

    return (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
            <Box sx={{ fontSize: '12px', color: tokens.color.ink2 }}>
                <FormattedMessage id="app.searchConsole.dictionary.intro" />
            </Box>

            <Box component="form" onSubmit={save} sx={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                <Select
                    size="small"
                    value={scope}
                    onChange={event => setScope(event.target.value as SearchDictionaryScope)}
                    sx={{ fontSize: '12px', minWidth: 180 }}
                >
                    {SCOPES.map(value => (
                        <MenuItem key={value} value={value} sx={{ fontSize: '12px' }}>
                            <FormattedMessage id={`app.searchConsole.dictionary.scope.${value}`} />
                        </MenuItem>
                    ))}
                </Select>
                <TextField
                    size="small"
                    value={term}
                    onChange={event => setTerm(event.target.value)}
                    placeholder={intl.formatMessage({ id: 'app.searchConsole.dictionary.termPlaceholder' })}
                    sx={{ width: 260 }}
                />
                <Box sx={{ color: tokens.color.ink3 }}>→</Box>
                <TextField
                    size="small"
                    value={replacement}
                    onChange={event => setReplacement(event.target.value)}
                    placeholder={intl.formatMessage({ id: 'app.searchConsole.dictionary.replacementPlaceholder' })}
                    sx={{ width: 300 }}
                />
                <Button type="submit" variant="contained" disabled={saving || !term.trim() || !replacement.trim()}>
                    <FormattedMessage id="app.searchConsole.dictionary.save" />
                </Button>
                {saveFailed ? (
                    <Box sx={{ fontSize: '12px', color: tokens.color.warnLine }}>
                        <FormattedMessage id="app.searchConsole.dictionary.saveFailed" />
                    </Box>
                ) : null}
            </Box>

            <Box sx={{ fontSize: '12px', color: tokens.color.ink3 }}>
                <FormattedMessage id="app.searchConsole.dictionary.count" values={{ count: entries.length }} />
                {entries.some(entry => 'suggested' === entry.status) ? (
                    <Box component="span" sx={{ marginLeft: '8px', color: tokens.color.warnInk }}>
                        <FormattedMessage
                            id="app.searchConsole.dictionary.waiting"
                            values={{ count: entries.filter(entry => 'suggested' === entry.status).length }}
                        />
                    </Box>
                ) : null}
            </Box>

            <PaloListView
                embedded={true}
                columns={columns}
                items={entries}
                getRowId={entry => `${entry.scope}:${entry.term}`}
                requesting={requesting}
                emptyLabel={<FormattedMessage id="app.searchConsole.dictionary.empty" />}
            />
        </Box>
    );
};
